import React from 'react'
import type { BoxProps } from '../../Box'
import { Box } from '../../Box'
import { H1 } from '../../Semantic'
import { Title } from '../../Title'
import { Typography } from '../../Typography'
import { spacing } from '../../_styles/spacing'
import {
  StyledActions,
  StyledPageHeader,
  StyledToggleActionBox,
} from './PageHeaderTemplate.styles'
import type { PageHeaderTemplateProps } from './PageHeaderTemplate.types'
import { usePageHeaderTemplateLayout } from './PageHeaderTemplate.utils'

const PageHeaderTemplateActions = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<Omit<BoxProps, 'as'>>
>(({ children, ...boxProps }, ref) => (
  <StyledActions {...boxProps} display="flex" gap="sm" ref={ref}>
    {children}
  </StyledActions>
))

export const PageHeaderTemplate = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<PageHeaderTemplateProps>
>(
  (
    {
      actions,
      banner,
      beforeTitleZone,
      breadcrumbs,
      children,
      heading,
      pills,
      subheading,
      tabs,
      toggleAction,
      ...props
    },
    ref
  ) => {
    const {
      headerRef,
      breadcrumbsWrapperRef,
      breadcrumbsRef,
      actionsRef,
      headingRef,
      actionsPosition,
    } = usePageHeaderTemplateLayout()

    const flexWrap = React.useMemo((): BoxProps['flexWrap'] => {
      if (actionsPosition === 'bottom') {
        return 'wrap'
      }

      return 'nowrap'
    }, [actionsPosition, breadcrumbs])

    const hasBottomRow = Boolean(tabs || toggleAction)

    return (
      <React.Suspense fallback={null}>
        <StyledPageHeader
          ref={ref}
          data-core-react="page-header-template"
          $hasPaddingBottom={!hasBottomRow}
          {...props}
        >
          <Box display="flex" flexDirection="column" gap="md">
            {banner}

            <Box
              display="flex"
              justifyContent="space-between"
              flexDirection={breadcrumbs ? 'column' : 'row'}
              gap={breadcrumbs ? undefined : 'md'}
              flexWrap={flexWrap}
              ref={headerRef}
            >
              {breadcrumbs && (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  gap="lg"
                  flexWrap="wrap"
                  ref={breadcrumbsWrapperRef}
                  style={{ minHeight: spacing.xl }}
                >
                  <Box marginBottom="md" ref={breadcrumbsRef}>
                    {breadcrumbs}
                  </Box>

                  {actionsPosition === 'top' && (
                    <PageHeaderTemplateActions ref={actionsRef}>
                      {actions}
                    </PageHeaderTemplateActions>
                  )}
                </Box>
              )}

              <Title ref={headingRef}>
                {beforeTitleZone && (
                  <Title.Assets>{beforeTitleZone}</Title.Assets>
                )}
                {heading && (
                  <Title.Text>
                    <H1>{heading}</H1>
                  </Title.Text>
                )}
                {pills && <Title.Pills>{pills}</Title.Pills>}
                {subheading && (
                  <Title.Subtext>
                    <Typography color="gray45">{subheading}</Typography>
                  </Title.Subtext>
                )}
              </Title>

              {(!breadcrumbs || actionsPosition === 'bottom') && (
                <PageHeaderTemplateActions
                  ref={actionsRef}
                  flexWrap={flexWrap}
                  marginTop={
                    breadcrumbs && actionsPosition === 'bottom'
                      ? 'md'
                      : undefined
                  }
                >
                  {actions}
                </PageHeaderTemplateActions>
              )}
            </Box>

            {/* 2025-3: Intentionally not supporting a generic zone with first release */}
            {/* {children} */}

            {hasBottomRow && (
              <Box
                display="flex"
                gap="xs"
                alignItems="flex-end"
                justifyContent={tabs ? 'space-between' : 'flex-end'}
              >
                {tabs}

                {toggleAction && (
                  <StyledToggleActionBox>{toggleAction}</StyledToggleActionBox>
                )}
              </Box>
            )}
          </Box>
        </StyledPageHeader>
      </React.Suspense>
    )
  }
)
